import { Field, FieldProps, FormikProps } from 'formik';
import * as React from 'react';
import { OperationVariables, QueryResult } from '@apollo/client';
import {
  FieldRow,
  StyledFormGroup,
  StyledFormSelectFormik,
  WrappedQuery,
  StyledFormikDatePicker,
} from '../../../../components/shared';
import {
  ALL_COUNTRIES,
  ALL_PROVINCES,
  FILTERED_PERSONNEL_POSITIONS,
  ALL_REGIONS,
  FILTERED_REASON_FOR_TERMINATIONS,
} from '../../../../graphql';
import { formatSelectValue } from '../../../../utils';
import { Option, PeopleProfileInstitutionFormType } from '../../../../utils/types';
import { InstitutionsCard } from './institutionsCard';
import { PersonDetailsFormType } from '../forms/PersonDetailsForm';

type FormType = PersonDetailsFormType & {
  typeId: Option | undefined;
  portal: string;

  branch: string;
  countryId: Option | undefined;
  dateExited?: Date | null;
  designation: string;
  districtId: Option | undefined;
  emailAddress: string;
  emisNumber: number | undefined;
  personnelPositionId: Option | undefined;
  personnelPositionOther: string;
  provinceId: Option | undefined;
  regionId: Option | undefined;
  terminationId: Option | undefined;

  primaryInstitutionId: Option | undefined;
  institutionIds: Option[];
  associatedInstitutions: PeopleProfileInstitutionFormType[];
};

type Props = {
  formikProps: FormikProps<FormType>;
  userRoles?: any;
};

const FieldsForProvincialOfficial: React.FC<Props> = props => {
  const { formikProps, userRoles } = props;
  const {
    values: { dateExited },
  } = formikProps;

  const canLinkInstitutions = ['INSTITUTION_ADMIN', 'INSTITUTION_MEMBER'].some(
    (item: any) => userRoles && userRoles.includes(item)
  );

  return (
    <React.Fragment>
      <FieldRow>
        <Field
          name="emailAddress"
          render={({ field }: FieldProps<FormType>) => (
            <StyledFormGroup {...field} field={field} form={formikProps} label="Email Address" mr={4} />
          )}
        />
        <WrappedQuery query={ALL_COUNTRIES} variables={{ input: {} }} overrideStates={true}>
          {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
            return (
              <Field
                name="countryId"
                render={({ field }: FieldProps<FormType>) => (
                  <StyledFormSelectFormik
                    {...field}
                    field={field}
                    label="Country"
                    form={formikProps}
                    options={
                      loading || error
                        ? []
                        : data.countries.map((item: any) =>
                            formatSelectValue({
                              label: item.name,
                              value: item.id,
                            })
                          )
                    }
                    mr={4}
                  />
                )}
              />
            );
          }}
        </WrappedQuery>
        {formikProps.values && formikProps.values.typeId && formikProps.values.typeId.label !== 'National Officials' && (
          <WrappedQuery query={ALL_PROVINCES} variables={{ input: {} }} overrideStates={true}>
            {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
              return (
                <Field
                  name="provinceId"
                  render={({ field }: FieldProps<FormType>) => (
                    <StyledFormSelectFormik
                      {...field}
                      field={field}
                      label="Province"
                      form={formikProps}
                      options={
                        loading || error
                          ? []
                          : data.provinces.map((item: any) =>
                              formatSelectValue({
                                label: item.name,
                                value: item.id,
                              })
                            )
                      }
                      mr={4}
                    />
                  )}
                />
              );
            }}
          </WrappedQuery>
        )}
        <Field
          name="branch"
          render={({ field }: FieldProps<FormType>) => (
            <StyledFormGroup {...field} field={field} form={formikProps} label="Branch" />
          )}
        />
      </FieldRow>
      <FieldRow>
        {formikProps.values && formikProps.values.typeId && formikProps.values.typeId.label !== 'National Officials' && (
          <WrappedQuery query={ALL_REGIONS} variables={{ input: {} }} overrideStates={true}>
            {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
              return (
                <Field
                  name="regionId"
                  render={({ field }: FieldProps<FormType>) => (
                    <StyledFormSelectFormik
                      {...field}
                      field={field}
                      label="Region"
                      form={formikProps}
                      options={
                        loading || error
                          ? []
                          : data.regions &&
                            data.regions.map((item: any) =>
                              formatSelectValue({
                                label: item.name,
                                value: item.id,
                              })
                            )
                      }
                      mr={4}
                    />
                  )}
                />
              );
            }}
          </WrappedQuery>
        )}

        <WrappedQuery
          query={FILTERED_PERSONNEL_POSITIONS}
          variables={{
            input: {
              typeId: formikProps.values.typeId && formikProps.values.typeId.value,
            },
          }}
          overrideStates={true}
        >
          {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
            return (
              <Field
                name="personnelPositionId"
                render={({ field }: FieldProps<FormType>) => (
                  <StyledFormSelectFormik
                    {...field}
                    field={field}
                    label="Personnel Position"
                    form={formikProps}
                    options={
                      loading || error
                        ? []
                        : data.filteredPersonnelPositions &&
                          data.filteredPersonnelPositions.map((item: any) =>
                            formatSelectValue({
                              label: item.name,
                              value: item.id,
                            })
                          )
                    }
                    mr={4}
                  />
                )}
              />
            );
          }}
        </WrappedQuery>

        {formikProps.values &&
          formikProps.values.personnelPositionId &&
          formikProps.values.personnelPositionId.label === 'Other' && (
            <Field
              name="personnelPositionOther"
              render={({ field }: FieldProps<FormType>) => (
                <StyledFormGroup {...field} field={field} form={formikProps} label="Personnel Position Other" mr={4} />
              )}
            />
          )}
        <Field
          name="designation"
          render={({ field }: FieldProps<FormType>) => (
            <StyledFormGroup {...field} field={field} form={formikProps} label="Designation" mr={4} />
          )}
        />
        <Field
          name="persalNumber"
          render={({ field }: FieldProps<FormType>) => (
            <StyledFormGroup {...field} field={field} form={formikProps} label="Persal Number" />
          )}
        />
      </FieldRow>
      <FieldRow>
        <Field
          render={() => (
            <StyledFormikDatePicker
              isSingleDate={true}
              form={formikProps}
              label="Date Exited / Termination"
              dateLabel="dateExited"
              date={dateExited || null}
              mr={4}
            />
          )}
        />
        <WrappedQuery
          query={FILTERED_REASON_FOR_TERMINATIONS}
          variables={{
            input: {
              typeId: formikProps.values.typeId && formikProps.values.typeId.value,
            },
          }}
          overrideStates={true}
        >
          {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
            return (
              <Field
                name="terminationId"
                render={({ field }: FieldProps<FormType>) => (
                  <StyledFormSelectFormik
                    {...field}
                    field={field}
                    label="Reason for Exit"
                    form={formikProps}
                    options={
                      loading || error
                        ? []
                        : data.filteredReasonForTerminations &&
                          data.filteredReasonForTerminations.map((item: any) =>
                            formatSelectValue({
                              label: item.name,
                              value: item.id,
                            })
                          )
                    }
                    // mr={2}
                  />
                )}
              />
            );
          }}
        </WrappedQuery>
      </FieldRow>
      <InstitutionsCard
        formikProps={formikProps}
        viewMode={formikProps.values.portal !== 'admin' && !canLinkInstitutions}
        currentInstitutionsOnly={formikProps.values.portal !== 'admin' && !canLinkInstitutions}
      />
    </React.Fragment>
  );
};

export default FieldsForProvincialOfficial;
